(() => {
  if (customElements.get('product-catalog-navigation')) return;

  class ProductCatalogNavigation extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.observer?.observe(this.panel);
        this.queueResize();
        this.schedulePreload();
        return;
      }
      if (!this.querySelector('[data-map-toggle]')) return;
      this.initialized = true;
      this.pageSize = 12;
      this.page = 0;
      this.preloadImages = new Map();
      this.preloadQueue = [];
      this.preloadActive = 0;
      this.preloadVersion = 0;
      this.toggle = this.querySelector('[data-map-toggle]');
      this.panel = this.querySelector('.product-catalog-navigation__map');
      this.reveal = this.querySelector('[data-map-reveal]');
      this.isOpen = false;
      this.grid = this.querySelector('[data-map-grid]');
      this.status = this.querySelector('[data-map-status]');
      this.toggle.hidden = false;
      this.panel.inert = true;
      this.panel.setAttribute('aria-hidden', 'true');
      this.toggle.addEventListener('click', () => {
        // A click after hover should not immediately undo the hover opening.
        if (this.openedByHover && this.isOpen) {
          this.openedByHover = false;
          clearTimeout(this.closeTimer);
          return;
        }
        this.setOpen(!this.isOpen);
      });
      this.toggle.addEventListener('pointerenter', (event) => {
        if (!this.canHover(event)) return;
        clearTimeout(this.closeTimer);
        clearTimeout(this.openTimer);
        if (!this.items) this.load();
        this.openTimer = setTimeout(() => {
          this.openedByHover = !this.isOpen;
          this.setOpen(true);
        }, 120);
      });
      this.addEventListener('pointerenter', (event) => {
        if (!this.canHover(event)) return;
        this.pointerInside = true;
        clearTimeout(this.closeTimer);
      });
      this.addEventListener('pointerleave', (event) => {
        if (!this.canHover(event)) return;
        this.pointerInside = false;
        clearTimeout(this.openTimer);
        this.scheduleClose();
      });
      this.addEventListener('focusin', () => clearTimeout(this.closeTimer));
      this.addEventListener('focusout', (event) => {
        if (!this.pointerInside && !this.contains(event.relatedTarget)) this.scheduleClose();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen) {
          this.setOpen(false);
          this.toggle.focus({ preventScroll: true });
        }
      });
      this.querySelector('[data-map-retry]').addEventListener('click', () => this.load());
      this.querySelectorAll('[data-map-page]').forEach((button) => {
        button.addEventListener('click', () => {
          this.page += Number(button.dataset.mapPage);
          this.render();
          // Keep keyboard focus usable even when the page control becomes disabled.
          this.grid.querySelector('a')?.focus({ preventScroll: true });
        });
      });
      if (window.ResizeObserver) {
        this.observer = new ResizeObserver(() => this.queueResize());
        this.observer.observe(this.panel);
      }
      this.schedulePreload();
    }

    disconnectedCallback() {
      this.controller?.abort();
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      cancelAnimationFrame(this.resizeFrame);
      this.observer?.disconnect();
      this.motion?.cancel();
      this.motion = null;
      this.removeAttribute('data-map-motion');
      window.removeEventListener('load', this.startPreload);
      window.cancelIdleCallback?.(this.preloadIdle);
      clearTimeout(this.preloadTimer);
      this.preloadVersion++;
      this.clearPreloadQueue();
    }

    canPreload() {
      const connection = navigator.connection;
      return !connection?.saveData && !['slow-2g', '2g'].includes(connection?.effectiveType);
    }

    schedulePreload() {
      if (this.hidden || !this.dataset.catalogUrl || !this.canPreload()) return;
      // Do not compete with the hero image or other initial page resources.
      this.startPreload = () => {
        const prepare = () => {
          if (!this.isConnected || !this.canPreload()) return;
          if (!this.items) this.load();
          else this.preloadVisibleImages();
        };
        if (window.requestIdleCallback) this.preloadIdle = window.requestIdleCallback(prepare, { timeout: 1200 });
        else this.preloadTimer = setTimeout(prepare, 250);
      };
      if (document.readyState === 'complete') this.startPreload();
      else window.addEventListener('load', this.startPreload, { once: true });
    }

    clearPreloadQueue() {
      this.preloadQueue?.forEach(({ key }) => this.preloadImages.delete(key));
      this.preloadQueue = [];
    }

    preloadVisibleImages() {
      const version = ++this.preloadVersion;
      this.clearPreloadQueue();
      const images = Array.from(this.grid.querySelectorAll('img'));
      // Eager loads also run while the drawer is closed. Keep the exact srcset
      // and sizes so opening the drawer uses the same responsive cached files.
      images.forEach((image) => {
        image.loading = 'eager';
        image.decoding = 'async';
        image.setAttribute('fetchpriority', 'low');
      });
      Promise.allSettled(images.map((image) => image.decode?.())).then(() => {
        if (!this.isConnected || version !== this.preloadVersion || !this.canPreload()) return;
        this.preloadAdjacentPages();
      });
    }

    preloadAdjacentPages() {
      // Warm only the two adjacent map pages, never all product images.
      for (const page of [this.page + 1, this.page - 1]) {
        if (page < 0 || page * this.pageSize >= this.items.length) continue;
        this.items.slice(page * this.pageSize, (page + 1) * this.pageSize).forEach((item) => {
          const image = item.querySelector('img');
          if (!image) return;
          const key = ['src', 'srcset', 'sizes'].map((name) => image.getAttribute(name) || '').join('|');
          if (this.preloadImages.has(key)) return;
          this.preloadImages.set(key, null);
          this.preloadQueue.push({ key, image });
        });
      }
      this.pumpPreloadQueue();
    }

    pumpPreloadQueue() {
      if (!this.isConnected || !this.canPreload()) return;
      while (this.preloadActive < 2 && this.preloadQueue.length) {
        const { key, image: source } = this.preloadQueue.shift();
        const image = new Image();
        this.preloadImages.set(key, image);
        this.preloadActive++;
        let finished = false;
        const finish = () => {
          if (finished) return;
          finished = true;
          image.onload = image.onerror = null;
          this.preloadActive--;
          // Bound retained decoded previews while browsing through the catalog.
          while (this.preloadImages.size > this.pageSize * 3) this.preloadImages.delete(this.preloadImages.keys().next().value);
          this.pumpPreloadQueue();
        };
        image.onload = () => Promise.resolve(image.decode?.()).catch(() => {}).finally(finish);
        image.onerror = () => { this.preloadImages.delete(key); finish(); };
        image.loading = 'eager';
        image.decoding = 'async';
        image.setAttribute('fetchpriority', 'low');
        // Set sizing before sources to avoid an unnecessary fallback request.
        for (const name of ['sizes', 'srcset', 'src']) {
          if (source.hasAttribute(name)) image.setAttribute(name, source.getAttribute(name));
        }
      }
    }

    canHover(event) {
      return event.pointerType !== 'touch' && window.matchMedia?.('(hover: hover) and (pointer: fine)').matches;
    }

    scheduleClose() {
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.setOpen(false), 180);
    }

    setOpen(open) {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      this.isOpen = open;
      if (!open) {
        this.openedByHover = false;
        if (this.panel.contains(document.activeElement)) this.toggle.focus({ preventScroll: true });
      }
      this.toggle.setAttribute('aria-expanded', String(open));
      this.panel.inert = !open;
      this.panel.setAttribute('aria-hidden', String(!open));
      if (open && !this.items) this.load();
      this.resizeReveal();
    }

    queueResize() {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        if (this.isConnected && this.isOpen) this.resizeReveal();
      });
    }

    resizeReveal() {
      if (!this.reveal) return;
      const wasHidden = this.reveal.hidden;
      const from = wasHidden ? 0 : this.reveal.getBoundingClientRect().height;
      const opacity = wasHidden ? 0 : Number(getComputedStyle(this.reveal).opacity);
      if (this.isOpen) this.reveal.hidden = false;
      const panelStyle = getComputedStyle(this.panel);
      const target = this.isOpen ? Math.ceil(this.panel.getBoundingClientRect().height + (parseFloat(panelStyle.marginTop) || 0) + (parseFloat(panelStyle.marginBottom) || 0)) : 0;
      if (!wasHidden && this.targetHeight === target && this.targetOpen === this.isOpen) return;
      this.targetHeight = target;
      this.targetOpen = this.isOpen;
      this.motion?.cancel();
      this.motion = null;
      this.setAttribute('data-map-motion', '');
      this.reveal.style.height = `${target}px`;
      this.reveal.style.opacity = this.isOpen ? '1' : '0';
      const finish = () => {
        this.removeAttribute('data-map-motion');
        if (!this.isOpen) this.reveal.hidden = true;
      };
      if (!this.reveal.animate || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || Math.abs(target - from) < 1) {
        finish();
        return;
      }
      // One height animation drives both the drawer and the content below it.
      // Retarget from the current height on resize, async loading or reversal.
      const motion = this.reveal.animate([
        { height: `${from}px`, opacity },
        { height: `${target}px`, opacity: this.isOpen ? 1 : 0 }
      ], { duration: this.isOpen ? 480 : 420, easing: 'cubic-bezier(.22, 1, .36, 1)' });
      this.motion = motion;
      motion.onfinish = () => {
        if (this.motion !== motion) return;
        this.motion = null;
        finish();
      };
    }

    async load() {
      if (this.loading) return;
      this.loading = true;
      this.controller = new AbortController();
      this.panel.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Loading catalog…';
      this.querySelector('[data-map-retry]').hidden = true;
      this.queueResize();
      try {
        const items = [];
        const visited = new Set();
        let url = new URL(this.dataset.catalogUrl, location.origin);
        // Preserve the selected theme when testing an unpublished preview.
        const previewTheme = new URL(location.href).searchParams.get('preview_theme_id');
        while (url) {
          if (url.origin !== location.origin) throw new Error('Unexpected catalog origin');
          url.searchParams.set('section_id', 'product-catalog-map');
          if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
          if (visited.has(url.href)) throw new Error('Repeated catalog page');
          visited.add(url.href);
          const response = await fetch(url.href, { credentials: 'same-origin', signal: this.controller.signal });
          if (!response.ok) throw new Error('Catalog request failed');
          const document = new DOMParser().parseFromString(await response.text(), 'text/html');
          const template = document.querySelector('template[data-catalog-map-items]');
          if (!template) throw new Error('Catalog data missing');
          items.push(...template.content.querySelectorAll('a[data-product-id]'));
          url = template.dataset.nextUrl ? new URL(template.dataset.nextUrl, url) : null;
        }
        this.items = items;
        const current = items.findIndex((item) => item.dataset.productId === this.dataset.productId);
        this.page = current < 0 ? 0 : Math.floor(current / this.pageSize);
        const position = this.querySelector('[data-map-position]');
        position.textContent = current < 0 ? '' : `${current + 1} / ${items.length}`;
        position.setAttribute('aria-label', current < 0 ? 'Catalog' : `Current design ${current + 1} of ${items.length}`);
        this.render();
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.status.textContent = 'Could not load the map. Try again or open View all.';
          this.querySelector('[data-map-retry]').hidden = false;
        }
      } finally {
        this.loading = false;
        this.panel.removeAttribute('aria-busy');
        this.queueResize();
      }
    }

    render() {
      const start = this.page * this.pageSize;
      const end = Math.min(start + this.pageSize, this.items.length);
      const fragment = document.createDocumentFragment();
      this.items.slice(start, end).forEach((item) => {
        const link = item.cloneNode(true);
        if (link.dataset.productId === this.dataset.productId) link.setAttribute('aria-current', 'page');
        fragment.append(link);
      });
      this.grid.replaceChildren(fragment);
      this.preloadVisibleImages();
      this.status.textContent = this.items.length ? '' : 'No designs in this catalog yet.';
      this.querySelector('[data-map-pagination]').hidden = this.items.length <= this.pageSize;
      this.querySelector('[data-map-range]').textContent = `${start + 1}–${end} / ${this.items.length}`;
      this.querySelector('[data-map-page="-1"]').disabled = this.page === 0;
      this.querySelector('[data-map-page="1"]').disabled = end >= this.items.length;
      this.queueResize();
    }
  }

  customElements.define('product-catalog-navigation', ProductCatalogNavigation);
})();
